// Minimal, dependency-free client-side query cache.
//
// PURPOSE: hot read queries (entitlement list, whoami, mastery-by-topic)
// were refetching on every navigation, since each screen mounts a fresh
// effect and fires the tRPC call again. This adds a tiny in-memory cache
// so a freshly-fetched result is reused within a short TTL window, and
// concurrent callers for the same key share a single in-flight request.
//
// This is DELIBERATELY lightweight — it is NOT a React-Query framework.
// It provides exactly three behaviors:
//   (a) TTL: a cached value newer than `ttlMs` is returned without refetch.
//   (b) In-flight dedup: concurrent callers with the same key await one
//       shared promise instead of issuing duplicate requests.
//   (c) Errors are NOT cached: a rejected fetch leaves nothing behind, so
//       the next caller retries cleanly.
//
// If/when caching needs grow (stale-while-revalidate, focus refetch,
// pagination, optimistic updates), replace this with @tanstack/react-query
// rather than extending this file.

const DEFAULT_TTL_MS = 30_000;

interface CacheEntry<T> {
  // Resolved value + when it was stored (for TTL checks). Present only
  // after a successful fetch.
  value: T;
  storedAt: number;
}

// Two maps: one for settled values (TTL-gated), one for in-flight promises
// (dedup). A key is in at most one meaningful state at a time, but keeping
// them separate keeps the TTL logic and the dedup logic independent.
const cache = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

/**
 * Run `fetcher` through the cache keyed by `key`.
 *
 * - Returns a cached value if one exists and is newer than `ttlMs`.
 * - Otherwise, if a request for this key is already in flight, returns that
 *   shared promise (dedup).
 * - Otherwise issues the fetch, caches the result on success, and (on
 *   failure) caches nothing so the next call retries.
 *
 * `key` must encode every distinct input (e.g. include the vertical slug
 * and jurisdiction for a scoped query) so different inputs don't collide.
 */
export async function cachedQuery<T>(
  key: string,
  fetcher: () => Promise<T>,
  opts?: { ttlMs?: number },
): Promise<T> {
  const ttlMs = opts?.ttlMs ?? DEFAULT_TTL_MS;

  const entry = cache.get(key) as CacheEntry<T> | undefined;
  if (entry && Date.now() - entry.storedAt < ttlMs) {
    return entry.value;
  }

  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const promise = (async () => {
    try {
      const value = await fetcher();
      cache.set(key, { value, storedAt: Date.now() });
      return value;
    } finally {
      // Always clear the in-flight slot — on success the value is now in
      // `cache`; on failure nothing is cached, so the next caller retries.
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
}

/**
 * Drop cached values. With no argument clears the entire cache; with a
 * `keyPrefix` clears only entries whose key starts with it (e.g.
 * `invalidateCachedQuery("mastery.byTopic:")`). In-flight requests are left
 * alone — they'll simply not populate a stale entry that's already gone.
 *
 * Call after a mutation that changes the underlying data (e.g. a purchase
 * invalidates `entitlement.list`, sign-out invalidates everything).
 */
export function invalidateCachedQuery(keyPrefix?: string): void {
  if (keyPrefix === undefined) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (key.startsWith(keyPrefix)) cache.delete(key);
  }
}
